/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { type ModalStatusType, type OptionType } from '../../../../types/common';
import ModalComponent from '../../../../components/modal/modal';
import ModalBodyComponent from '../../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../../components/modal/modalFooterComponent';
import InputText from '../../../../components/input-text/inputText';
import CustomTextArea from '../../../../components/text-area/textArea';
import _, { isEmpty } from 'lodash';
import FormSelect from '../../../../components/dropdown/formSelect';
import * as Yup from 'yup';
import InitialValuesForm from '../../../../constants/initialValues';
import useWealthlaneTaxApi from '../../../../hooks/useWealthlaneTaxApi';
import {
  useGetIncomeYtdTypes,
  useGetTaxCityOptions,
  useGetTaxJuridictionLevel,
  useGetTaxPeriodAll,
  usePostTaxSetup,
} from '../../../../hooks';
import {
  JurisdictionLevelComponent,
  TaxBracketComponent,
  UserDefinedCriteriaComponent,
} from './tax-block';
import ApplicableComponent from './tax-block/applicableComponent';
import {
  type Wealthlane_TaxService_Dtos_TaxItemDto_CreateTaxItemRequestDto,
  type Wealthlane_TaxService_Dtos_TaxItemDto_JsonDto,
  type Wealthlane_TaxService_Dtos_TaxItemDto_TaxItemDto,
} from '@wealthlane/api-services/wealthlane-tax-service';
import { TAX_SETUP_VALIDATION } from '../../../../utils/validations/validations';
import useGetTaxNameOptions from '../../../../hooks/get/useGetTaxNameOptions';
import useGetTaxCountryOptionsWithJuridiction from '../../../../hooks/get/useGetTaxCountryOptionsWithJuridiction';
import useGetTaxStateOptionsByJurisdiction from '../../../../hooks/get/useGetTaxStateOptionsByJurisdiction';
import { DescriptionType } from './taxConstantTypes';
import { validateCriteria } from '../../../../utils/validations/customValidation';

interface TaxSetupType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  setModalStatus: (value: ModalStatusType) => void;
  loading?: boolean;
  initialValue?: Wealthlane_TaxService_Dtos_TaxItemDto_TaxItemDto | null;
  id?: string;
}

const TaxSetupModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  setModalStatus,
  loading,
  initialValue,
  id,
}: TaxSetupType): JSX.Element => {
  const optionState = {
    juridictionLevelId: initialValue?.jurisdictionLevelId ?? '',
    countryId: initialValue?.countryId ?? '',
    stateId: initialValue?.stateId ?? '',
  };
  const [optionSelectedState, setOptionSelectedState] =
    useState<Record<string, string>>(optionState);

  const { handleRequest } = useWealthlaneTaxApi();
  const { data: taxPeriodOption, isLoading: isTaxPeriodOptionLoading } =
    useGetTaxPeriodAll(handleRequest);
  const { data: juridictionLevelOptions, isLoading: isJuridictionLevelOptionsLoading } =
    useGetTaxJuridictionLevel(handleRequest);
  const [descriptionData, setDescriptionData] = useState<DescriptionType[]>([]);
  const { data: countryOptions } = useGetTaxCountryOptionsWithJuridiction(
    handleRequest,
    optionSelectedState?.juridictionLevelId
  );
  const { data: stateOptions } = useGetTaxStateOptionsByJurisdiction(
    handleRequest,
    optionSelectedState?.juridictionLevelId,
    optionSelectedState?.countryId !== '' ? optionSelectedState?.countryId : initialValue?.countryId
  );
  const { data: cityOptions } = useGetTaxCityOptions(
    handleRequest,
    optionSelectedState?.stateId !== '' ? optionSelectedState?.stateId : initialValue?.stateId
  );
  const [metaValidation, setMetaValidation] = useState<
    Wealthlane_TaxService_Dtos_TaxItemDto_JsonDto | undefined
  >();

  const [applicableName, setApplicableName] = useState<string>('');
  const { data: parentTax, isLoading: isParentTaxLoading } = useGetTaxNameOptions(handleRequest);

  const { mutate, isLoading } = usePostTaxSetup(handleRequest, closeModal, modalStatus);
  const handleSubmit = (
    values: Wealthlane_TaxService_Dtos_TaxItemDto_CreateTaxItemRequestDto
  ): void => {
    mutate(values);
  };

  // useEffect(() => {
  //   optionSelectedState?.stateArrayIndex !== undefined &&
  //     setStateArrayOptions({
  //       ...stateArrayOptions,
  //       [`${optionSelectedState?.stateArrayIndex}`]: stateOptions,
  //     });
  // }, [optionSelectedState?.stateArrayIndex, stateOptions]);

  // useEffect(() => {
  //   optionSelectedState?.cityArrayIndex !== undefined &&
  //     setCityArrayOptions({
  //       ...cityArrayOptions,
  //       [`${optionSelectedState?.cityArrayIndex}`]: cityOptions,
  //     });
  // }, [optionSelectedState?.cityArrayIndex, cityOptions]);
  const [filteredValue, setFilteredValue] = useState<any>();

  const groupData = () => {
    let group =
      descriptionData !== undefined &&
      descriptionData[0]?.field !== undefined &&
      descriptionData[0]?.groupLogicalOperator !== undefined &&
      _(descriptionData)
        .groupBy('groupLogicalOperator')
        .map((data, groupLogicalOperator) => {
          let field = _(data)
            ?.groupBy('field')
            ?.map((item, field) => {
              let equality = _(item)
                ?.groupBy('equalityOperator')
                ?.map((equals, equalityOperator) => {
                  let name = equals.map((country: any) => country.name);
                  return { equalityOperator, name };
                })
                .value();
              return { field, equality };
            })
            .value();
          return {
            field,
            groupLogicalOperator,
          };
        })
        .value();
    setFilteredValue(group);
    return group;
  };

  const { data: incomeYtdOptions, isLoading: isIncomeYtdOptionsLoading } =
    useGetIncomeYtdTypes(handleRequest);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValue ?? InitialValuesForm.taxSetup}
      validationSchema={Yup.object().shape(TAX_SETUP_VALIDATION(metaValidation, applicableName))}
      validate={(values) => {
        const errors: any = {};
        if (values.userDefinedCriteria) {
          const criteriaErrors = validateCriteria(values.userDefinedCriteria);
          if (Object.keys(criteriaErrors).length > 0) {
            errors.userDefinedCriteria = criteriaErrors;
          }
        }
        return errors;
      }}
      onSubmit={(values: any) => {
        // const udcPayload = values?.userDefinedCriteria !== null && {
        //   ...values.userDefinedCriteria,
        //   Conditions: values?.userDefinedCriteria?.Conditions?.map((item: any, i: number) => ({
        //     ...item,
        //     Field: values?.userDefinedCriteria?.Conditions[i]?.SubCriteria?.Conditions[0]?.Field,
        //     Value: values?.userDefinedCriteria?.Conditions[i]?.SubCriteria?.Conditions[0]?.Value,
        //     Name: values?.userDefinedCriteria?.Conditions[i]?.SubCriteria?.Conditions[0]?.Name,
        //     EqualityOperatorType:
        //       values?.userDefinedCriteria?.Conditions[i]?.SubCriteria?.Conditions[0]
        //         ?.EqualityOperatorType,
        //     SubCriteria: {
        //       ...values?.userDefinedCriteria?.Conditions[i]?.SubCriteria,
        //       Conditions:
        //         item?.SubCriteria?.Conditions?.length > 1
        //           ? item?.SubCriteria?.Conditions?.slice(1)
        //           : [],
        //     },
        //   })),
        // };

        const payload = {
          name: values?.name?.trim(),
          jurisdictionLevelId: values?.jurisdictionLevelId,
          taxPeriodId: values?.taxPeriodId,
          taxCode: values?.taxCode,
          equityCompensationLevelId: values?.equityCompensationLevelId,
          countryId: values?.countryId,
          stateId: values?.stateId,
          stateText: values?.stateText,
          cityId: values?.cityId,
          cityText: values?.cityText,
          participantId: values?.participantId,
          planId: values?.planId,
          grantId: values?.grantId,
          awardId: values?.awardId,
          transactionId: values?.transactionId,
          id: modalStatus === 'Add' ? null : !isEmpty(values?.id) ? values?.id : null,
          taxBrackets: values?.taxBrackets?.map((item: any) => ({
            lowerLimit: Number(String(item?.lowerLimit)?.replaceAll(',', '') ?? ''),
            taxRate: Number(String(item?.taxRate)?.replaceAll(',', '') ?? ''),
            // upperLimit: Number(String(item?.upperLimit)?.replaceAll(',', '') ?? ''),
          })),
          incomeYtdTypeId: values?.incomeYtdTypeId ? values?.incomeYtdTypeId : null,
          comment: values?.comment?.trim() || null,
          userDefinedCriteria:
            values?.userDefinedCriteria !== null
              ? values?.userDefinedCriteria?.Conditions[0]?.SubCriteria?.Conditions[0]?.Field ===
                  null ??
                values?.userDefinedCriteria?.Conditions[0]?.SubCriteria?.Conditions[0]?.Field === ''
                ? null
                : JSON.stringify(values?.userDefinedCriteria)
              : null,
        };

        handleSubmit(payload);
      }}>
      {({ touched, handleChange, errors, setFieldValue, setValues, setTouched, values }: any) => {
        const metaData: Wealthlane_TaxService_Dtos_TaxItemDto_JsonDto | undefined =
          juridictionLevelOptions?.filter(
            (item: OptionType) => item?.value === values?.jurisdictionLevelId
          )[0]?.metadata;

        useEffect(() => {
          setMetaValidation(metaData);
        }, [metaData]);

        useEffect(() => {
          groupData();
        }, [descriptionData, values]);

        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            loading={loading}
            modalStatus={modalStatus}
            valueEdited={_.isEqual(
              values,
              initialValue ? initialValue : InitialValuesForm.taxSetup
            )}
            size='lg'
            title={`${modalStatus === 'Add' ? 'Create' : modalStatus} Tax Setup`}
            id={id}
            children={
              <Form id='taxSetupForm'>
                <ModalBodyComponent id='taxSetupModalBody'>
                  <div className='' id='taxSetupFormContainer'>
                    <div
                      className='grid lg:grid-cols-12 md:grid-cols-3 sm:grid-cols-1 gap-x-8'
                      id='taxSetupBasicInfoSection'>
                      <div
                        className='lg:col-span-5  md:grid-cols-3  sm:col-span-1'
                        id='taxSetupNameContainer'>
                        <InputText
                          type='text'
                          label='Name'
                          name='name'
                          id='name'
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          required
                        />
                      </div>
                      <div className='lg:col-span-3 sm:col-span-1' id='taxSetupTaxPeriodContainer'>
                        <FormSelect
                          label={'Tax Period'}
                          name='taxPeriodId'
                          id='taxPeriodId'
                          containerClassName='formGroup'
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          options={taxPeriodOption}
                          isLoading={isTaxPeriodOptionLoading}
                          values={values}
                          setFieldValue={setFieldValue}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          menuWidth={'300px'}
                          required
                        />
                      </div>
                      <div
                        className='lg:col-span-4 sm:col-span-1'
                        id='taxSetupJurisdictionLevelContainer'>
                        <FormSelect
                          type='text'
                          label={'Jurisdiction Level'}
                          name='jurisdictionLevelId'
                          id='jurisdictionLevelId'
                          containerClassName='formGroup'
                          onChange={(e: any) => {
                            const meta = juridictionLevelOptions?.filter(
                              (item: OptionType) => item?.value === e
                            );
                            setOptionSelectedState({
                              ...optionSelectedState,
                              countryId:
                                meta[0]?.label?.includes('State') || meta[0]?.label === 'Local'
                                  ? ''
                                  : optionSelectedState?.countryId,
                              stateId: '',
                            });

                            setFieldValue('countryId', null);
                            setFieldValue('stateId', null);
                            setFieldValue('cityId', null);
                            setFieldValue('cityText', null);
                            setFieldValue('stateText', null);
                            setFieldValue(
                              'userDefinedCriteria',
                              meta[0]?.metadata?.allowUserDefinedCriteria
                                ? InitialValuesForm?.taxSetup?.userDefinedCriteria
                                : null
                            );
                            setDescriptionData([]);

                            // const spoofCountryId = juridictionLevelOptions?.find(
                            //   (item: OptionType) => item?.label === 'Country'
                            // )?.value;

                            setOptionSelectedState({
                              ...optionSelectedState,
                              juridictionLevelId: e,
                              countryId: '',
                              stateId: '',
                              // jurisdictionLabel: meta[0]?.label,
                            });
                            handleChange(e);
                          }}
                          errors={errors}
                          touched={touched}
                          options={juridictionLevelOptions}
                          isLoading={isJuridictionLevelOptionsLoading}
                          values={values}
                          setFieldValue={setFieldValue}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          required
                        />
                      </div>
                      <div
                        className='lg:col-span-5  md:grid-cols-3  sm:col-span-1'
                        id='taxSetupPayCodeContainer'>
                        <InputText
                          type='text'
                          label='Pay Code'
                          name='taxCode'
                          id='taxCode'
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          required={false}
                        />
                      </div>
                      {values?.parentTaxItemId !== undefined && values?.parentTaxItemId !== null && (
                        <div
                          className='lg:col-span-4 sm:col-span-1'
                          id='taxSetupParentTaxContainer'>
                          <FormSelect
                            type='text'
                            label={'Parent Tax'}
                            name='parentTaxItemId'
                            id='parentTaxItemId'
                            containerClassName='formGroup'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            options={parentTax}
                            isLoading={isParentTaxLoading}
                            values={values}
                            setFieldValue={setFieldValue}
                            inputType={modalStatus}
                            disabled
                            required
                          />
                        </div>
                      )}
                      <div className='col-span-12' id='taxSetupCommentContainer'>
                        <CustomTextArea
                          label='Comment'
                          name='comment'
                          id='comment'
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          inputType={modalStatus === 'View' ? 'View' : undefined}
                          disabled={modalStatus === 'View'}
                          rows='3'
                          required={false}
                          placeholder=''
                        />
                      </div>
                    </div>

                    {/* Jurisdiction Level */}
                    {Boolean(values?.jurisdictionLevelId) && (
                      <JurisdictionLevelComponent
                        metaData={metaData}
                        countryOptions={countryOptions}
                        stateOptions={stateOptions}
                        cityOptions={cityOptions}
                        setOptionSelectedState={setOptionSelectedState}
                        optionSelectedState={optionSelectedState}
                        handleChange={handleChange}
                        errors={errors}
                        touched={touched}
                        values={values}
                        setFieldValue={setFieldValue}
                        modalStatus={modalStatus}
                        initialValue={initialValue}
                        descriptionData={descriptionData}
                        filteredValue={filteredValue}
                      />
                    )}
                    {/* User Defined Criteria */}
                    {(metaData?.allowUserDefinedCriteria ?? false) && (
                      <UserDefinedCriteriaComponent
                        handleRequest={handleRequest}
                        metaData={metaData}
                        handleChange={handleChange}
                        errors={errors}
                        touched={touched}
                        values={values}
                        setFieldValue={setFieldValue}
                        modalStatus={modalStatus}
                        jurisdictionOptions={juridictionLevelOptions}
                        setDescriptionData={setDescriptionData}
                        descriptionData={descriptionData}
                      />
                    )}

                    {/* Tax Bracket */}
                    <TaxBracketComponent
                      metaData={metaData}
                      handleChange={handleChange}
                      errors={errors}
                      touched={touched}
                      values={values}
                      modalStatus={modalStatus}
                      setFieldValue={setFieldValue}
                      setValues={setValues}
                      setTouched={setTouched}
                    />
                    <div className='grid md:grid-cols-2 sm:grid-cols-2 mt-4'>
                      <FormSelect
                        type='text'
                        label={'Income Ytd Type'}
                        id='incomeYtdTypeId'
                        name='incomeYtdTypeId'
                        onChange={(e: any) => {
                          handleChange(e);
                        }}
                        errors={errors}
                        touched={touched}
                        options={incomeYtdOptions}
                        isLoading={isIncomeYtdOptionsLoading}
                        values={values}
                        setFieldValue={setFieldValue}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        required
                      />
                    </div>

                    {/* Applicable TO */}
                    <ApplicableComponent
                      setApplicableName={setApplicableName}
                      handleRequest={handleRequest}
                      handleChange={handleChange}
                      errors={errors}
                      touched={touched}
                      values={values}
                      setFieldValue={setFieldValue}
                      modalStatus={modalStatus}
                    />
                  </div>
                </ModalBodyComponent>
                <ModalFooterComponent
                  id='taxSetupModalFooter'
                  modalStatus={modalStatus}
                  loading={isLoading}
                  titleCollection={{ reviewTitle: 'Review & Confirm', cancelTitle: 'Go Back' }}
                  valueChanged={_.isEqual(
                    values,
                    initialValue != null ? initialValue : InitialValuesForm.taxSetup
                  )}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  onEditModal={() => {
                    values?.statusSystemName === 'Created' && setModalStatus('Edit');
                  }}
                  isEditShow={values?.statusSystemName === 'Created'}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default TaxSetupModal;
